import { RefObject, useEffect, useState } from 'react';

/**
 * Detects whether the detail card actually overflows vertically (shows a scrollbar).
 * When it does not, the card must not trap the wheel: callers switch its
 * overscroll-behavior to `auto` so scrolling chains to the parent grid (JS-1073).
 *
 * `overflow-y: auto` must stay on the element so its clientHeight remains clamped
 * by max-height; otherwise scrollHeight would always equal clientHeight and
 * overflow could never be detected again.
 */
export const useDetailScrollbarDetection = (
  ref: RefObject<HTMLElement | null>,
  dependency: unknown
) => {
  const [hasScrollbar, setHasScrollbar] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => setHasScrollbar(el.scrollHeight > el.clientHeight);

    check();
    const observer = new ResizeObserver(() => requestAnimationFrame(check));
    observer.observe(el);

    return () => observer.disconnect();
  }, [ref, dependency]);

  return hasScrollbar;
};
